/**
 * HTML Tag Auto-Complete Utility
 * Automatically completes HTML closing tags when user types opening tags
 */

// Tags that should be auto-completed (self-closing tags excluded)
export const AUTO_COMPLETE_TAGS = [
  'ul', 'ol', 'li',
  'b', 'i', 'u', 'strong', 'em', 'code', 'mark',
  'p', 'div', 'span',
  'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'tr', 'td', 'th', 'thead', 'tbody',
  'pre', 'blockquote', 'sup', 'sub'
];

/**
 * Checks if HTML tag auto-completion should be performed and returns the result
 * @param {string} value - Current textarea value
 * @param {number} cursorPos - Current cursor position
 * @param {string} prevValue - Previous textarea value (before this change)
 * @returns {object|null} - { newValue, cursorPos } if auto-completion needed, null otherwise
 */
export const checkHtmlTagAutoComplete = (value, cursorPos, prevValue = '') => {
  // Only trigger if the user just typed a single '>' character
  // This prevents triggering when cursor moves or during composition
  if (prevValue) {
    const lengthDiff = value.length - prevValue.length;
    // Only trigger when exactly one character was added
    if (lengthDiff !== 1) {
      return null;
    }
    // Check if the added character is '>'
    if (value[cursorPos - 1] !== '>') {
      return null;
    }
  } else {
    // Fallback: check if character before cursor is '>'
    if (value[cursorPos - 1] !== '>') {
      return null;
    }
  }

  // Find the opening tag before cursor
  const textBeforeCursor = value.substring(0, cursorPos);

  // Match opening tag pattern: <tagname> or <tagname attribute="value">
  // But NOT closing tags </tagname> or self-closing <tagname />
  const openingTagMatch = textBeforeCursor.match(/<([a-zA-Z][a-zA-Z0-9]*)[^>]*>$/);

  if (!openingTagMatch) {
    return null;
  }

  const tagName = openingTagMatch[1].toLowerCase();

  // Check if this tag should be auto-completed
  if (!AUTO_COMPLETE_TAGS.includes(tagName)) {
    return null;
  }

  // Check if it's a self-closing tag (ends with />)
  if (openingTagMatch[0].endsWith('/>')) {
    return null;
  }

  // Check if closing tag already exists right after cursor
  const textAfterCursor = value.substring(cursorPos);
  const closingTag = `</${tagName}>`;

  if (textAfterCursor.startsWith(closingTag)) {
    return null;
  }

  // Also check if the closing tag already exists anywhere after the opening tag
  // This prevents duplicate tags when editing
  if (textAfterCursor.includes(closingTag)) {
    // Count opening and closing tags to see if they're balanced
    const fullText = value;
    const openingRegex = new RegExp(`<${tagName}(?:\\s[^>]*)?>`, 'gi');
    const closingRegex = new RegExp(`</${tagName}>`, 'gi');
    const openCount = (fullText.match(openingRegex) || []).length;
    const closeCount = (fullText.match(closingRegex) || []).length;

    if (closeCount >= openCount) {
      return null;
    }
  }

  // Insert closing tag
  const newValue = value.substring(0, cursorPos) + closingTag + value.substring(cursorPos);

  return {
    newValue,
    cursorPos // Cursor stays at current position (between opening and closing tags)
  };
};

/**
 * Handles HTML tag auto-completion on a textarea/input
 * Call this in onChange handler after setting the value
 * @param {HTMLElement} element - The textarea/input element
 * @param {string} value - The new value (after user input)
 * @param {function} setValue - Function to update the value state
 * @param {string} prevValue - Optional previous value for better detection
 */
export const handleHtmlTagAutoComplete = (element, value, setValue, prevValue = '') => {
  const cursorPos = element.selectionStart;
  const result = checkHtmlTagAutoComplete(value, cursorPos, prevValue);

  if (result) {
    setValue(result.newValue);

    // Set cursor position after React re-renders
    setTimeout(() => {
      element.selectionStart = result.cursorPos;
      element.selectionEnd = result.cursorPos;
      element.focus();
    }, 0);

    return true;
  }

  return false;
};

export default {
  checkHtmlTagAutoComplete,
  handleHtmlTagAutoComplete,
  AUTO_COMPLETE_TAGS
};
